import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewContainerRef
} from '@angular/core'
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { ActivatedRoute, UrlSegment } from '@angular/router'
import { Store } from '@ngrx/store'
import { CartUiEventAddToCart } from '@spartacus/cart/base/root'
import { RoutingService, SiteContextSelectors, StateWithSiteContext } from '@spartacus/core'
import { LaunchDialogService } from '@spartacus/storefront'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { bufferCount, distinctUntilChanged, filter, first, map, take, tap } from 'rxjs/operators'
import { EB_ICON_TYPE } from '../../../../components/icons/interface/eb-icon-type.enum'
import { EbOrderEntry } from '../../../cart/interface/eb-order-entry.interface'
import { EbActiveCartService } from '../../../cart/services/eb-active-cart.service'
import { EbFavorites } from '../../interfaces/eb-favorites.interface'
import { EbFavoritesService } from '../../services/facades/eb-favorites.service'

@Component({
    selector: 'eb-favorites',
    templateUrl: './eb-favorites.component.html',
    styleUrls: ['./eb-favorites.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EbFavoritesComponent implements OnInit, OnDestroy {
  selectedItemList$: Observable<EbFavorites>
  sortEntriesBy$ = new BehaviorSubject<string>('creationTime')
  filterEntriesBy$ = new BehaviorSubject('')
  form: FormGroup
  numberOfEntriesBeforeAdd = 0
  sortOptions = [
    { id: 'creationTime', name: 'favorites.details.sort.creationTime' },
    { id: 'name', name: 'favorites.details.sort.name' }
  ]
  selectedOption = this.sortOptions[0].id
  private readonly subscriptions = new Subscription()
  isVisible = true
  isSearchActive = false
  currentListId = ''

  constructor(
    private readonly ebFavoritesService: EbFavoritesService,
    private readonly routingService: RoutingService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly modalService: LaunchDialogService,
    private readonly activeCartService: EbActiveCartService,
    private readonly siteContextState: Store<StateWithSiteContext>,
    private readonly cd: ChangeDetectorRef,
    protected ebElement: ElementRef,
    protected vcr: ViewContainerRef
  ) {}

  get IconType() {
    return EB_ICON_TYPE
  }

  ngOnInit() {
    this.fetchList()
    this.listenToContextChanges()
    this.selectedItemList$ = this.ebFavoritesService.getList()
  }

  getSelectedFavoriteListId(segments: UrlSegment[]) {
    return segments[segments.length - 1].path === 'favorites'
      ? 'recommendations'
      : segments[segments.length - 1].path
  }

  fetchList() {
    this.subscriptions.add(
      this.activatedRoute.url
        .pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(current) === JSON.stringify(previous)
          }),
          tap(() => (this.isVisible = true)),
          map(urlSegments => {
            const listId = this.getSelectedFavoriteListId(urlSegments)
            this.form = this.fb.group({
              positions: this.fb.array([])
            })
            if (listId !== this.currentListId) {
              this.filterEntriesBy$.next('')
              this.sortEntriesBy$.next('creationTime')
            }
            this.currentListId = listId
            this.ebFavoritesService.fetchFavoriteListDetails(listId)
          })
        )
        .subscribe()
    )
  }

  listenToContextChanges() {
    this.subscriptions.add(
      this.siteContextState
        .select(SiteContextSelectors.getActiveLanguage)
        .pipe(
          bufferCount(2, 1),
          filter(([previous]) => !!previous),
          first()
        )
        .subscribe(() => {
          this.ngOnInit()
        })
    )
  }

  sortEntries(sortOption: string) {
    this.sortEntriesBy$.next(sortOption)
  }

  searchOnFavorites(searchTerm: string) {
    this.filterEntriesBy$.next(searchTerm)
  }

  clearSearchTerm(): void {
    this.filterEntriesBy$.next('')
    this.cd.detectChanges()
  }

  getI18nToken(details: EbFavorites) {
    return details.recommendationsList
      ? 'favorites.details.recommendations.created'
      : 'favorites.details.created'
  }

  isFavoriteSelected(): boolean {
    const positions = this.form.controls.positions as FormArray
    return positions.controls.length > 0
  }

  isAllSelected(entries: EbOrderEntry[]) {
    const positions = this.form.controls.positions as FormArray
    return entries.length === positions.controls.length
  }

  collectSelectedItem(item: EbOrderEntry) {
    const positions = this.form.controls.positions as FormArray
    if (item.isSelected) {
      positions.push(new FormControl(item))
    } else {
      const index = positions.controls.findIndex(x => x.value.product.code === item.product.code)
      positions.removeAt(index)
    }
  }

  collectAllSelectedItems(items: EbOrderEntry[]) {
    const positions = this.form.controls.positions as FormArray
    items.forEach(item => {
      if (item.isSelected) {
        const index = positions.controls.findIndex(x => x.value.product.code === item.product.code)
        if (index < 0) {
          positions.push(new FormControl(item))
        }
      } else {
        const index = positions.controls.findIndex(x => x.value.product.code === item.product.code)
        positions.removeAt(index)
      }
    })
  }

  removeSelectedItem(productCode: string, list: EbFavorites) {
    this.ebFavoritesService.removeEntryFromFavorites(
      list,
      list.entries.find(value => value.product.code === productCode)
    )
  }

  updateCommentOnEntry(item: { entryNumber: number; comment: string }, list: EbFavorites) {
    this.ebFavoritesService.updateCommentOnEntry(item, list)
  }

  sendAllItemsRfq() {
    const positions = this.form.controls.positions as FormArray
    const codes = positions.controls
      .filter(
        value =>
          !this.itemHasPrice(value) ||
          (!this.itemIsOrderable(value) && !this.itemHasSuccessor(value)) ||
          (this.itemHasSuccessor(value) &&
            (!this.successorIsOrderable(value) || !this.successorHasPrice(value)))
      )
      .map(value =>
        value.value.product?.successor
          ? value.value.product?.successor.code
          : value.value.product.code
      )
      .join(', ')
    this.routingService.goByUrl(`/rfp?productCode=${codes}`)
  }

  addFavoriteEntriesToCart() {
    const positions = this.form.controls.positions as FormArray
    const entriesToAdd = positions.controls
      .filter(
        value =>
          (this.itemHasPrice(value) && this.itemIsOrderable(value)) ||
          (this.itemHasSuccessor(value) &&
            this.successorHasPrice(value) &&
            this.successorIsOrderable(value))
      )
      .map(value => {
        return {
          product: {
            code: value.value.product?.successor
              ? value.value.product?.successor.code
              : value.value.product.code
          },
          quantity: 1
        } as EbOrderEntry
      })
    this.activeCartService
      .getEntries()
      .pipe(take(1))
      .subscribe(entries => {
        this.numberOfEntriesBeforeAdd = entries.length
        this.activeCartService.addEntriesToCart(entriesToAdd)
        this.openAddEntriesToCartModal(entriesToAdd)
      })
  }

  deleteFavoriteEntriesFromList(list: EbFavorites) {
    const positions = this.form.controls.positions as FormArray
    this.ebFavoritesService.removeSelectedEntriesFromFavorites(
      list,
      positions.controls.map(value => {
        return value.value
      }) as EbOrderEntry[]
    )
    // remove entries from form control
    positions.clear()
  }

  toggleSharedStatusOfList(favorites: EbFavorites) {
    this.ebFavoritesService.toggleSharedStatusOfList(favorites, !favorites.shared)
  }

  saveFavoritesName(favorites: EbFavorites) {
    const listName = document.getElementById('editListName').innerText
    if (listName.trim() !== '') {
      this.ebFavoritesService.updateNameOfFavorites(favorites, listName)
    }
    this.toggleEditableState()
  }

  toggleEditableState() {
    this.isVisible = !this.isVisible
  }

  itemHasPrice(value: AbstractControl): boolean {
    return value.value.totalPrice?.value && value.value.totalPrice?.value > 0
  }

  itemIsOrderable(value: AbstractControl): boolean {
    return value.value.product.orderable
  }

  itemHasSuccessor(value: AbstractControl): boolean {
    return !value.value.product.orderable && value.value.product?.successor
  }

  successorIsOrderable(value: AbstractControl): boolean {
    return value.value.product?.successor?.orderable
  }

  successorHasPrice(value: AbstractControl): boolean {
    return (
      value.value.product?.successor &&
      value.value.totalPrice?.value &&
      value.value.totalPrice?.value > 0
    )
  }

  openAddEntriesToCartModal(entriesToAdd: EbOrderEntry[]) {
    const modalData: CartUiEventAddToCart = new CartUiEventAddToCart()
    const positions = this.form.controls.positions as FormArray
    if (entriesToAdd.length > 0) {
      modalData.productCode = entriesToAdd[0].product.code
    }
    modalData.numberOfEntriesBeforeAdd =
      this.numberOfEntriesBeforeAdd === 0
        ? this.numberOfEntriesBeforeAdd
        : positions.controls.map(value => value.value).length -
          entriesToAdd.length +
          this.numberOfEntriesBeforeAdd
    const dialog = this.modalService.openDialog(
      'ADDED_TO_CART_FAVORITE',
      this.ebElement,
      this.vcr,
      modalData
    )
    dialog?.pipe(take(1)).subscribe()
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe()
  }
}
